import { apiClient } from '../lib/apiClient';
import { fetchGalleryImages } from './galleryService';

/**
 * Change categories an AI comparison result can be classified under.
 * Mirrors src/services/aquariumComparisonSchema.ts CHANGE_CATEGORY_VALUES.
 */
export type ComparisonChangeCategory =
  | 'none'
  | 'growth'
  | 'colour_change'
  | 'algae'
  | 'livestock_change'
  | 'other';

/** Confidence level of the AI comparison result. */
export type ComparisonConfidence = 'high' | 'medium' | 'low';

/** Whether the comparison used only fixed-camera photos ('full') or any manual uploads ('limited'). */
export type ComparisonComparability = 'full' | 'limited';

/** How the comparison was created. */
export type ComparisonTriggeredBy = 'manual' | 'scheduled';

/** The AI-generated qualitative comparison result. */
export interface ComparisonResultPayload {
  changeCategory: ComparisonChangeCategory;
  description: string;
  confidence: ComparisonConfidence;
  sourceImageIds: string[];
}

/**
 * A single persisted comparison, as returned by
 * POST /api/v1/aquariums/:aquariumId/comparisons (201 `{ data }`) and
 * GET /api/v1/aquariums/:aquariumId/comparisons/:comparisonId (200 `{ data }`).
 * Mirrors ComparisonRecord in src/services/aquariumComparisonService.ts.
 */
export interface ComparisonDetail {
  id: string;
  aquariumId: string;
  /** Resolved input image ids, earlier photo first. */
  imageIds: string[];
  comparability: ComparisonComparability;
  result: ComparisonResultPayload;
  triggeredBy: ComparisonTriggeredBy;
  createdAt: string;
}

/** One history list item — the list endpoint returns the same record shape as the detail. */
export type ComparisonListItem = ComparisonDetail;

/** Paginated list result from GET /api/v1/aquariums/:aquariumId/comparisons. */
export interface ListComparisonsResponse {
  items: ComparisonListItem[];
  nextCursor: string | null;
}

/**
 * Request payload for POST /api/v1/aquariums/:aquariumId/comparisons.
 * Exactly one of imageIds / dateOffsetDays / (dateFrom+dateTo) must be supplied —
 * the backend Zod schema rejects more than one of these shapes.
 */
export type CreateComparisonPayload =
  | { imageIds: string[] }
  | { dateOffsetDays: 7 | 30 | 90 }
  | { dateFrom: string; dateTo: string };

/**
 * Lists past comparisons for an aquarium, newest first, cursor-paginated.
 */
export async function listComparisons(
  aquariumId: string,
  cursor?: string | null,
): Promise<ListComparisonsResponse> {
  // apiClient does not serialise `params`, so the query string is built here.
  const query = cursor ? new URLSearchParams({ cursor }).toString() : '';
  const response = await apiClient.get<{ data: ComparisonListItem[]; nextCursor: string | null }>(
    `/api/v1/aquariums/${aquariumId}/comparisons${query ? `?${query}` : ''}`,
  );
  return { items: response.data, nextCursor: response.nextCursor ?? null };
}

/**
 * Fetches a single comparison's detail (read-only reopen from history).
 */
export async function getComparison(
  aquariumId: string,
  comparisonId: string,
): Promise<ComparisonDetail> {
  const response = await apiClient.get<{ data: ComparisonDetail }>(
    `/api/v1/aquariums/${aquariumId}/comparisons/${comparisonId}`,
  );
  return response.data;
}

/**
 * Deletes one comparison (and the AI memory fact it wrote). 404 when it is not in this aquarium.
 */
export async function deleteComparison(aquariumId: string, comparisonId: string): Promise<void> {
  await apiClient.delete(`/api/v1/aquariums/${aquariumId}/comparisons/${comparisonId}`);
}

/**
 * Deletes every comparison of the aquarium (and their AI memory facts); resolves with the count.
 */
export async function deleteAllComparisons(aquariumId: string): Promise<number> {
  const response = await apiClient.delete<{ data?: { deleted?: number } }>(
    `/api/v1/aquariums/${aquariumId}/comparisons`,
  );
  return response?.data?.deleted ?? 0;
}

/**
 * Creates (runs) a new comparison. Synchronous — resolves once the AI call completes.
 */
export async function createComparison(
  aquariumId: string,
  payload: CreateComparisonPayload,
): Promise<ComparisonDetail> {
  const response = await apiClient.post<{ data: ComparisonDetail }>(
    `/api/v1/aquariums/${aquariumId}/comparisons`,
    payload,
  );
  return response.data;
}

/** Display data of one aquarium image referenced by a comparison. */
export interface ComparisonImageInfo {
  url: string | null;
  /**
   * ISO 8601 timestamp of when the photo was taken: the API's photoTakenAt
   * (camera capture time, else manual/EXIF taken date), else the upload date.
   */
  date: string;
}

const IMAGE_LOOKUP_PAGE_SIZE = 100;
const IMAGE_LOOKUP_MAX_PAGES = 10;

/**
 * Resolves the display URL and date of the given aquarium images via the
 * aquarium gallery list (there is no single-image endpoint). Pages through the
 * gallery until every id is found or the gallery is exhausted.
 */
export async function fetchComparisonImages(
  aquariumId: string,
  imageIds: string[],
): Promise<Record<string, ComparisonImageInfo>> {
  const missing = new Set(imageIds);
  const lookup: Record<string, ComparisonImageInfo> = {};
  for (let page = 1; missing.size > 0 && page <= IMAGE_LOOKUP_MAX_PAGES; page += 1) {
    const response = await fetchGalleryImages({ aquariumId, page, limit: IMAGE_LOOKUP_PAGE_SIZE });
    for (const image of response.images) {
      if (missing.has(image.id)) {
        lookup[image.id] = {
          url: image.displayUrl ?? image.thumbnailUrl,
          date: image.photoTakenAt ?? image.uploadedAt,
        };
        missing.delete(image.id);
      }
    }
    if (!response.hasMore) break;
  }
  return lookup;
}

/** Selection limits of the "choose photos" mode — mirror src/services/aquariumComparisonSchema.ts. */
export const MIN_COMPARISON_IMAGES = 2;
export const MAX_COMPARISON_IMAGES = 5;

/** A photo the owner picked for an explicit comparison. */
export interface PickedComparisonPhoto {
  id: string;
  /** ISO 8601 photo date (photoTakenAt, else upload date). */
  takenAt: string;
}

function photoTime(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
}

/**
 * Orders picked photos earliest first (undated or unparsable dates last,
 * ties by id), so a request reads before → after whatever order the photos
 * were clicked in.
 */
export function sortPhotosByDate<T extends PickedComparisonPhoto>(photos: readonly T[]): T[] {
  return [...photos].sort((a, b) => {
    const ta = photoTime(a.takenAt);
    const tb = photoTime(b.takenAt);
    if (ta !== tb) return ta < tb ? -1 : 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

/** Builds the `{ imageIds }` payload for explicitly chosen photos, earliest photo first. */
export function buildImageIdsPayload(photos: readonly PickedComparisonPhoto[]): { imageIds: string[] } {
  return { imageIds: [...new Set(sortPhotosByDate(photos).map(photo => photo.id))] };
}

/** One page of the aquarium gallery, as shown in the photo picker. */
export interface ComparisonGalleryPage {
  photos: Array<PickedComparisonPhoto & { thumbnailUrl: string | null }>;
  page: number;
  hasMore: boolean;
}

/** Fetches one gallery page (GET /api/v1/aquariums/:id/images) for the photo picker. */
export async function fetchComparisonGalleryPage(
  aquariumId: string,
  page: number,
  limit: number,
): Promise<ComparisonGalleryPage> {
  const response = await fetchGalleryImages({ aquariumId, page, limit });
  return {
    photos: response.images.map(image => ({
      id: image.id,
      takenAt: image.photoTakenAt ?? image.uploadedAt,
      thumbnailUrl: image.thumbnailUrl ?? image.displayUrl,
    })),
    page,
    hasMore: response.hasMore,
  };
}
